import React, { useState } from "react";
import { Link } from "react-router-dom";
import { SiteResponse } from "api/ApiClient";
import type { SiteStatus } from "../AdminSites/SiteStatusModal";
import { statusBadgeVariant } from "shared/utils/statusChipColor";
import { usePropertySites } from "./hooks/usePropertyData";
import { Badge } from "components/ui/badge";
import { Spinner } from "components/ui/spinner";

const VISIBLE_SITE_LIMIT = 4;

interface PropertySiteCardsProps {
  propertyExternalId: string;
}

// api/ApiClient's generated SiteResponse hasn't caught up with the backend's Status field yet
interface PropertySiteResponse extends SiteResponse {
  status?: SiteStatus | null;
}

function SiteCard({ site }: { site: PropertySiteResponse }) {
  return (
    <Link
      className="block min-w-80 max-w-100 rounded-md border bg-card p-3 shadow-sm transition-colors hover:bg-accent"
      to={`/admin/sites/${site.id}`}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="truncate text-sm font-semibold">
              {site.name}
        </span>
        <Badge variant={statusBadgeVariant(site.status)}>
          {site.status ?? "Staging"}
        </Badge>
      </div>
      <span className="text-xs text-muted-foreground">{site.systemSiteId}</span>
    </Link>
  );
}


export default function PropertySiteCards({ propertyExternalId }: PropertySiteCardsProps) {
  const { data: sites, isLoading } = usePropertySites(propertyExternalId);
  const [showAll, setShowAll] = useState(false);

  if (isLoading) {
    return <Spinner className="size-5" />;
  }

  if (!sites || sites.length === 0) {
    return null;
  }

  const visibleSites = showAll ? sites : sites.slice(0, VISIBLE_SITE_LIMIT);
  const overflowCount = sites.length - visibleSites.length;

  return (
    <div className="flex max-w-[1800px] flex-wrap gap-2">
      {visibleSites.map((site) => (
        <SiteCard key={site.id} site={site} />
      ))}
      {overflowCount > 0 && (
        <button
          className="min-w-50 rounded-md border bg-card p-3 text-sm font-semibold shadow-sm transition-colors hover:bg-accent"
          onClick={() => setShowAll(true)}
          type="button"
        >
          +{overflowCount} more
        </button>
      )}
    </div>
  );
}
